import React from 'react'
import { Button } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faBagShopping,
  faMoon,
  faUmbrellaBeach,
} from '@fortawesome/free-solid-svg-icons'
import CartItem from './CartItem/CartItem'
import { IProduct } from '../../types/products'
import { useAppNavigate } from '../../context/AppContext'
import { useCartMinimums } from '../../utils/useCartMinimums'
import { useStoreHours } from '../../utils/useStoreHours'
import { getGeneralInfo } from '../../utils/generalInfo'
import { cartCount, cartTotal } from '../../utils/cart'
import { PandaSign } from '../panda-sign/PandaSign'
import './Cart.scss'

type Props = {
  cart: IProduct[]
  setCart: React.Dispatch<React.SetStateAction<IProduct[]>>
}
const c = 'cart-card'

const Cart = (props: Props) => {
  const navigate = useAppNavigate()
  const { checkedCart } = useCartMinimums(props.cart)
  const { t } = useTranslation()
  const { isOutsideHours } = useStoreHours()
  const generalInfo = getGeneralInfo()
  const totalPrice = cartTotal(props.cart)
  const minPrice = Number(generalInfo?.minPrice) || 0
  const belowMinimum = totalPrice < minPrice

  function disableCart() {
    if (generalInfo.dayOff) {
      return true
    }

    if (isOutsideHours) {
      return true
    }

    if (!props.cart.length) {
      return true
    }

    if (belowMinimum) {
      return true
    }

    return false
  }

  return (
    <div className={c} data-cart-target>
      <div className={`${c}__header`}>
        <span className={`${c}__header-icon`}>
          <FontAwesomeIcon icon={faBagShopping} />
        </span>
        {t('CART')}
        {props.cart.length > 0 && (
          <span key={cartCount(props.cart)} className={`${c}__count`}>
            {cartCount(props.cart)}
          </span>
        )}
      </div>

      <div className={`${c}__items`}>
        {props.cart?.length ? (
          checkedCart.map((item: IProduct) => (
            <CartItem key={item._id} data={item} setCart={props.setCart} />
          ))
        ) : (
          <div className={`${c}__empty`}>
            <PandaSign size={60}>{t('NO_PRODUCT_SELECTED')}</PandaSign>
          </div>
        )}
      </div>

      {props.cart.length > 0 && belowMinimum && (
        <div className={`${c}__progress`}>
          <div className={`${c}__progress-bar`}>
            <span style={{ width: `${Math.min(100, (totalPrice / minPrice) * 100)}%` }} />
          </div>
          <Trans
            i18nKey='MIN_ORDER_LEFT'
            values={{ amount: minPrice - totalPrice }}
          />
        </div>
      )}

      <div className={`${c}__total`}>
        <span>{t('TOTAL')}</span>
        <strong>{totalPrice} lei</strong>
      </div>

      {belowMinimum && (
        <div className={`${c}__note`}>
          <Trans i18nKey='MINIMUM_PRICE' values={{ minPrice: generalInfo?.minPrice }} />
        </div>
      )}

      <Button
        variant='contained'
        disabled={disableCart()}
        onClick={() => navigate('/cart')}
        endIcon={<FontAwesomeIcon icon={faArrowRight} />}
        sx={{
          backgroundColor: 'var(--color-primary)',
          width: '100%',
          '&:hover': { backgroundColor: 'var(--color-primary-hover)' },
        }}
      >
        {t('CART')}
      </Button>

      {isOutsideHours && !generalInfo.dayOff && (
        <div className={`${c}__alert`}>
          <FontAwesomeIcon icon={faMoon} />{' '}
          <Trans
            i18nKey='TIME_LIMIT'
            values={{ minTime: generalInfo?.minTime, maxTime: generalInfo.maxTime }}
          />
        </div>
      )}
      {generalInfo?.dayOff && (
        <div className={`${c}__alert`}>
          <FontAwesomeIcon icon={faUmbrellaBeach} /> <Trans i18nKey='DAY_OFF' />
        </div>
      )}
    </div>
  )
}

export default Cart
